/**
 * VH-253: one thing to buy.
 *
 * The WHOLE row is the tick target — not a checkbox. A 20px box is a miss waiting to
 * happen with a trolley in the other hand; the row is 56px tall and the entire width.
 * Deleting is a separate small button on the right, far from the tick, because
 * "remove" and "bought" are different decisions and a mis-tap between them costs the
 * item.
 */

import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'

export default function ShoppingRow({ item, bought = false, onToggle, onRemove }) {
  const { t } = useTranslation()
  const Icon = bought ? CheckCircleSolid : CheckCircleIcon

  return (
    <li className="flex items-stretch border-b border-outline-variant last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(item, !bought)}
        // `min-w-0`: without it this flex child cannot shrink below its content, the inner
        // `truncate` never gets a bounded width, and one long name scrolls the whole page
        // sideways — which slides the delete button under the thumb aiming to tick (QA,
        // 2026-09-24: 646px of scroll width at a 375px viewport).
        className="flex min-w-0 flex-1 items-center gap-3 px-3 py-3 min-h-[56px] text-left active:bg-surface-bright"
        aria-pressed={bought}
        aria-label={
          bought
            ? t('shopping.row.undoAria', { name: item.name })
            : t('shopping.row.tickAria', { name: item.name })
        }
        data-testid={`shopping-row-${item.id}`}
      >
        <Icon
          className={`h-6 w-6 shrink-0 ${bought ? 'text-success-600' : 'text-on-surface-variant'}`}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-base ${
              bought ? 'text-on-surface-variant line-through' : 'text-on-surface font-medium'
            }`}
          >
            {item.name}
            {item.quantity ? (
              <span className="ml-2 text-sm font-normal text-on-surface-variant">
                {item.quantity}
              </span>
            ) : null}
          </span>
          {item.note ? (
            <span className="block truncate text-xs text-on-surface-variant/80">
              {item.note}
            </span>
          ) : null}
        </span>
      </button>

      {onRemove ? (
        <button
          type="button"
          onClick={() => onRemove(item)}
          className="px-3 text-on-surface-variant hover:text-danger-600"
          aria-label={t('shopping.row.removeAria', { name: item.name })}
          data-testid={`shopping-remove-${item.id}`}
        >
          <TrashIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      ) : null}
    </li>
  )
}
